import {
  Divider,
  Flex,
  Group,
  NumberInput,
  Select,
  Title,
} from "@mantine/core";
import { toNumber } from "lodash";
import React, { useEffect, useState } from "react";
import { notification } from "../../../../../../utils/notification";

type insertDotMode = "row" | "column" | null;

interface InsertAdditionalSeatsProps {
  px?: string;
  selectedKey: string;
  setSelectedKey: React.Dispatch<React.SetStateAction<string>>;
  data: any;
  setData: React.Dispatch<React.SetStateAction<any>>;
  setUndoStack: React.Dispatch<React.SetStateAction<any[]>>;
  t: (key: string) => string;
}

const InsertAdditionalSeats: React.FC<InsertAdditionalSeatsProps> = ({
  selectedKey,
  setSelectedKey,
  px,
  data,
  setData,
  setUndoStack,
  t,
}) => {
  const [rowOrColumn, setRowOrColumn] = useState<insertDotMode>("row");
  const [stepX, setStepX] = useState<number>(20);
  const [newX, setNewX] = useState<number>(20);
  const [newY, setNewY] = useState<number>(20);

  useEffect(() => {
    if (!selectedKey) {
      return;
    }
    const [name, row, column] = selectedKey.split(":");
    const keys = Object.keys(data.data);

    setUndoStack((prevStack) => [...prevStack, data]);

    let newData = { ...data.data };

    switch (rowOrColumn) {
      case "row": {
        const indexRows = keys.map((key) => parseInt(key.split(":")[1], 10)); // extract IndexRow
        const lastIndexRow = Math.max(...indexRows);

        const newKey = `${name}:${lastIndexRow + 1}:${1}`;

        newData[newKey] = {
          rect: {
            x: newX.toString(),
            y: newY.toString(),
          },
        };

        break;
      }

      case "column": {
        const matchingParters = Object.keys(data.data).filter((key) =>
          key.includes(name)
        );
        const lastMatch = matchingParters[matchingParters.length - 1];
        const [_, __, colIndexLast] = lastMatch.split(":").map(toNumber);

        if (matchingParters.length <= 0) {
          return;
        }

        const lastSelectedKey = `${name}:${row}:${column}`;
        const lastDot = newData[lastSelectedKey];

        if (!lastDot) {
          notification({
            type: "error",
            title: "Missing Precedent Dot",
            message: "Were unable to locate the previous dot.",
          });

          return;
        }

        const newKey = `${name}:${row}:${+column + 1}`;
        newData[newKey] = {
          rect: {
            x: (+lastDot.rect.x + +stepX).toString(),
            y: (+lastDot.rect.y).toString(),
          },
        };
        break;
      }

      default:
        break;
    }

    setData((prevData: any) => ({
      ...prevData,
      data: newData,
    }));
  }, [selectedKey]);

  return (
    <>
      <Divider mt={15} mb={10} />

      <Group px={px}>
        <Title order={3}>
          {t("scheme.redactor.panelOptions.insertSeatInRowOrColMode.title")}
        </Title>

        <Select
          w={"100%"}
          data={[
            {
              value: "row",
              label: t(
                "scheme.redactor.panelOptions.insertSeatInRowOrColMode.insertTypeSelection.row"
              ),
            },
            {
              value: "column",
              label: t(
                "scheme.redactor.panelOptions.insertSeatInRowOrColMode.insertTypeSelection.col"
              ),
            },
          ]}
          value={rowOrColumn || undefined}
          onChange={(value) => setRowOrColumn(value as insertDotMode)}
        />

        {rowOrColumn === "row" && (
          <>
            <Flex gap={10}>
              <NumberInput
                min={0}
                onChange={(value) => {
                  setNewX(value as number);
                }}
                value={newX}
                label={t("scheme.redactor.panelOptions.setFigure.rect.x")}
              />

              <NumberInput
                min={0}
                onChange={(value) => {
                  setNewY(value as number);
                }}
                value={newY}
                label={t("scheme.redactor.panelOptions.setFigure.rect.y")}
              />
            </Flex>
          </>
        )}

        {rowOrColumn === "column" && (
          <NumberInput
            w={"50%"}
            min={0}
            onChange={(value) => {
              setStepX(value as number);
            }}
            value={stepX}
            label={t("scheme.redactor.panelOptions.setSector.stepsX")}
          />
        )}
      </Group>
    </>
  );
};

export default React.memo(InsertAdditionalSeats);
